/**
 * Event-driven 8-bit sound effects engine. Mirrors the native
 * `KokoSoundEffects.swift`:
 *
 *   - Master volume 0.35 (intentionally low — sits *under* the user's
 *     own audio, not on top).
 *   - Preloaded `HTMLAudioElement` pool so trigger points have zero
 *     network / decode latency.
 *   - 7 discrete events matching the native enum.
 *
 * When `sfxEnabled` is off, `play()` is a no-op so the event-wiring
 * stays the same regardless of user preference.
 */

import type { SettingsStore } from "../app/SettingsStore";

export type SoundEffectName =
  | "wake"
  | "listen"
  | "think"
  | "done"
  | "point"
  | "error"
  | "toggle";

const MASTER_VOLUME = 0.35;

const SFX_FILENAMES: Record<SoundEffectName, string> = {
  wake: "chirp_wake.wav",
  listen: "chirp_listen.wav",
  think: "hum_think.wav",
  done: "trill_done.wav",
  point: "ding_point.wav",
  error: "tone_error.wav",
  toggle: "click_toggle.wav",
};

export interface SoundEffectsOptions {
  audioBaseUrl: string;
  settings: SettingsStore;
}

export class SoundEffects {
  private readonly audioBaseUrl: string;
  private readonly settings: SettingsStore;
  private readonly preloadedAudio: Record<SoundEffectName, HTMLAudioElement>;

  constructor(options: SoundEffectsOptions) {
    this.audioBaseUrl = options.audioBaseUrl.replace(/\/$/, "");
    this.settings = options.settings;

    // Preload every clip once. Cloning on play() gives overlapping playback
    // (if two events fire in quick succession, neither clobbers the other).
    this.preloadedAudio = {} as Record<SoundEffectName, HTMLAudioElement>;
    (Object.keys(SFX_FILENAMES) as SoundEffectName[]).forEach((name) => {
      const audio = new Audio(`${this.audioBaseUrl}/${SFX_FILENAMES[name]}`);
      audio.preload = "auto";
      audio.volume = MASTER_VOLUME;
      this.preloadedAudio[name] = audio;
    });
  }

  /**
   * Play a sound effect by name. No-op if SFX are disabled or the browser
   * rejects playback (e.g. autoplay gating before first interaction).
   */
  play(name: SoundEffectName): void {
    if (!this.settings.get("sfxEnabled")) return;
    const source = this.preloadedAudio[name];
    if (!source) return;
    // Clone so overlapping events don't cut each other off.
    const instance = source.cloneNode(true) as HTMLAudioElement;
    instance.volume = MASTER_VOLUME;
    void instance.play().catch(() => {
      // Autoplay policy: first play before any user interaction will throw.
      // That's fine — by the time a user actually interacts, play() succeeds.
    });
  }
}
